import { useWallet } from '@solana/wallet-adapter-react';
import { useState } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { getErrorMessage } from '../../utils/errorHandling';
import { captureAndReportError } from '../../utils/errorReporting';
import { getNewVersionBlockedMessage } from '../../utils/publicationAccess';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import { useDappContext } from '../DappLayout';
import { useNewVersionDappData } from './hooks/useNewVersionDappData';
import { useNewVersionSubmission } from './hooks/useNewVersionSubmission';
import { NewVersionPageHeader } from './NewVersionPageHeader';
import type { ProcessingStatus, UploadState } from './types';
import {
  type PackageCaseRepair,
  VersionUploadContainer,
} from './VersionUploadContainer';
import { PublisherFrozenBanner } from '../PublisherFrozenBanner';

export function NewVersionPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const wallet = useWallet();
  const { dappId, packageName } = useDappContext();
  const dappRouteDapp = { id: dappId, packageName };
  const [authState] = useAuth();
  const { activePublisher } = usePublisher();

  usePageTitle('Update App | Publishing Portal');

  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRepairingPackage, setIsRepairingPackage] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('form');
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>(
    {},
  );

  const { dappData, dappName, isCheckingKyb } = useNewVersionDappData({
    authState,
    dappId,
    dappRouteDapp,
    navigate,
    trpc,
  });
  const publisherVerification = getPublisherVerificationGate({
    status: activePublisher?.kybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });
  const verificationBlockedMessage = publisherVerification.isApproved
    ? undefined
    : publisherVerification.newVersionMessage;
  const canCreateUpdate =
    !!dappData &&
    !getNewVersionBlockedMessage(dappData) &&
    !verificationBlockedMessage;

  const markProcessingStart = () => {
    setProcessingStatus((prev) => ({
      ...prev,
      phase: 'preparing',
      progress: 0,
      message: 'Preparing portal ingestion',
      error: undefined,
    }));
  };

  const resetProcessingFlow = () => {
    setProcessingStatus({});
  };

  const { handleSubmit } = useNewVersionSubmission({
    dappData,
    dappId,
    dappRouteDapp,
    markProcessingStart,
    navigate,
    wallet,
    resetProcessingFlow,
    setIsSubmitting,
    setProcessingStatus,
    setUploadState,
    trpc,
  });

  useBeforeUnload((event) => {
    if (isSubmitting) {
      event.preventDefault();
      return '';
    }
  });

  if (isCheckingKyb) {
    return (
      <div className="bg-[#161717] h-full flex items-center justify-center">
        <div className="text-neutral-400">Checking verification status…</div>
      </div>
    );
  }

  const submitDisabled =
    !canCreateUpdate ||
    !isFormValid ||
    isSubmitting ||
    !!processingStatus.error;

  const clearError = () => {
    setProcessingStatus({});
    setUploadState('form');
    setIsSubmitting(false);
  };

  const repairPackageCase = async (repair: PackageCaseRepair) => {
    setIsRepairingPackage(true);
    try {
      const result = await trpc.repairDappPackageCase.mutate({
        dappId,
        currentPackageName: repair.currentPackageName,
        newPackageName: repair.newPackageName,
      });

      if (result._tag === 'Left') {
        throw new Error(result.left.message);
      }

      clearError();
      toast.success('App package updated. Submit the release again.');
    } catch (error) {
      captureAndReportError(error, {
        operation: 'newVersion.repairDappPackageCase',
        component: 'NewVersionPage',
        dappId,
      });
      toast.error(
        getErrorMessage(error, 'Failed to update the app package name'),
      );
    } finally {
      setIsRepairingPackage(false);
    }
  };

  return (
    <div className="bg-[#161717] h-full flex flex-col">
      <NewVersionPageHeader
        isFormTouched={isFormTouched}
        isFormValid={isFormValid}
        isSubmitting={isSubmitting}
        hasError={!!processingStatus.error}
        isSubmitDisabled={submitDisabled}
        disabledTooltip={verificationBlockedMessage}
      />
      <PublisherFrozenBanner />
      <div className="flex justify-center p-6 bg-[#161717] text-white flex-1 overflow-y-auto">
        <div className="w-full max-w-2xl">
          <VersionUploadContainer
            dappName={dappName}
            onSubmit={handleSubmit}
            uploadState={uploadState}
            status={processingStatus}
            errorMessage={processingStatus.error || undefined}
            onClearError={clearError}
            onValidityChange={setIsFormValid}
            onTouchedChange={setIsFormTouched}
            isSubmitting={isSubmitting}
            isRepairingPackage={isRepairingPackage}
            onRepairPackageCase={repairPackageCase}
          />
        </div>
      </div>
    </div>
  );
}
